import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AudiqMark, AudiqWordmark, RuleBicolor } from '@audiq/ui';
import type { FlowWithRelations } from '@audiq/shared';
import { api } from '../../lib/api';

interface Props {
  flow: FlowWithRelations;
}

export function PreviewTab({ flow }: Props) {
  const [showPrompt, setShowPrompt] = useState(false);
  const promptQuery = useQuery({
    queryKey: ['preview-prompt', flow.id],
    queryFn: () => api.getPreviewPrompt(flow.id),
    enabled: showPrompt,
  });

  const opening =
    flow.persona.opening_message?.trim() ||
    `Ciao, sono ${flow.persona.name}. Faccio un po' di domande prima della tua richiesta.`;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
      <div className="space-y-4">
        <div
          className="rounded-md border p-5"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <div className="mono mb-3" style={{ color: 'var(--mute)' }}>
            Anteprima conversazione
          </div>

          <div className="flex items-start gap-3">
            <AudiqMark size={48} state="idle" />
            <div className="flex-1">
              <div className="flex items-baseline gap-2">
                <AudiqWordmark size="sm" />
                <span className="text-xs" style={{ color: 'var(--mute)' }}>
                  · {flow.persona.role}
                </span>
              </div>
              <Bubble role="bot">{opening}</Bubble>
            </div>
          </div>

          <RuleBicolor className="my-4" />

          <div className="space-y-3">
            {flow.questions.slice(0, 4).map((q) => (
              <div key={q.id} className="ms-12">
                <Bubble role="bot">{q.ask_examples?.[0] || `[Objective: ${q.objective}]`}</Bubble>
              </div>
            ))}
            {flow.questions.length === 0 && (
              <p className="ms-12 text-sm" style={{ color: 'var(--mute)' }}>
                (nessuna domanda configurata — vai sulla tab Domande)
              </p>
            )}
            {flow.questions.length > 4 && (
              <p className="ms-12 mono" style={{ color: 'var(--mute)' }}>
                + altre {flow.questions.length - 4} domande
              </p>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowPrompt((s) => !s)}
          className="rounded-md border px-4 py-2 text-sm"
          style={{
            background: 'var(--paper)',
            borderColor: 'var(--line)',
            color: 'var(--ink)',
          }}
        >
          {showPrompt ? 'Nascondi system prompt' : 'Mostra system prompt LLM'}
        </button>

        {showPrompt && promptQuery.isLoading && <p style={{ color: 'var(--mute)' }}>Carico…</p>}
        {showPrompt && promptQuery.data && (
          <pre
            className="rounded-md border p-4 font-mono text-xs leading-relaxed"
            style={{
              background: 'var(--soft)',
              borderColor: 'var(--line)',
              maxHeight: '600px',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              color: 'var(--ink)',
            }}
          >
            {promptQuery.data}
          </pre>
        )}
      </div>

      <aside
        className="rounded-md border p-5"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
      >
        <h3 className="mono mb-3" style={{ color: 'var(--mute)' }}>
          Riepilogo flow
        </h3>
        <dl className="space-y-2 text-sm">
          <Row k="Domande" v={String(flow.questions.length)} />
          <Row k="Esiti" v={String(flow.outcomes.length)} />
          <Row k="Boundaries" v={String(flow.boundaries.length)} />
          <Row k="Lingua default" v={flow.language_default} />
          <Row k="Stato" v={flow.is_active ? 'attivo' : 'draft'} />
          <Row
            k="Identità pre-chat"
            v={
              flow.settings.ask_personal_info?.enabled
                ? `${flow.settings.ask_personal_info.timing} (${flow.settings.ask_personal_info.fields.length} campi)`
                : 'no'
            }
          />
          <Row k="Emergency exit" v={flow.settings.emergency_exit?.enabled ? 'attiva' : 'no'} />
        </dl>
      </aside>
    </div>
  );
}

function Bubble({ role, children }: { role: 'bot' | 'user'; children: React.ReactNode }) {
  return (
    <div
      className="mt-2 inline-block rounded-2xl px-3 py-2 text-sm"
      style={{
        background: role === 'bot' ? 'var(--surface)' : 'var(--primary)',
        color: role === 'bot' ? 'var(--ink)' : '#fff',
        border: role === 'bot' ? '1px solid var(--line)' : 'none',
        borderRadius: role === 'bot' ? '16px 16px 16px 4px' : '16px 16px 4px 16px',
        maxWidth: '90%',
      }}
    >
      {children}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="mono" style={{ color: 'var(--mute)' }}>
        {k}
      </dt>
      <dd>{v}</dd>
    </div>
  );
}
